import { useEffect, useState } from "react";
import { Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, ts } from "@/lib/api";
import { Panel, Th, Td, btn } from "@/components/ui-kit";

const TONE = { sent: "text-green-300", generated: "text-sky-300", duplicate: "text-slate-400", skipped: "text-slate-400" };

const Row = ({ r, load }) => {
  const del = () => {
    if (!window.confirm(`Delete billing record ${r.invoice_no || r.filename}? You can then drop the file in again. Invoices already in the EDI Log are kept.`)) return;
    api.delete(`/sap-invoices/${r.id}`).then(load).catch((e) => toast.error(errMsg(e)));
  };
  return (
    <tr data-testid={`sap-invoice-${r.id}`}>
      <Td className="text-xs">{ts(r.created_at)}</Td><Td className="font-mono text-xs">{r.invoice_no || "-"}</Td><Td className="font-mono text-xs">{r.po_number || "-"}</Td>
      <Td className="text-xs">{r.partner || "-"}</Td>
      <Td className={`text-xs ${TONE[r.status] || "text-rose-300"}`} data-testid={`sap-invoice-status-${r.id}`}>{r.status}{r.gross != null ? ` · ${Number(r.gross).toFixed(2)} ${r.currency || ""}` : ""}{r.error && <div className="text-rose-300">{r.error}</div>}{r.sent && r.sent !== "sent" && <div className="text-slate-400">{r.sent}</div>}</Td>
      <Td className="text-xs text-slate-500">{r.filename}</Td>
      <Td><button type="button" onClick={del} className="text-slate-500 hover:text-rose-400" data-testid={`sap-invoice-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button></Td>
    </tr>
  );
};

export const SapInvoicesPanel = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/sap-invoices").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = (await api.post("/sap-invoices/upload", fd)).data;
        if (r.error) toast.error(`${f.name}: ${r.error}`);
        r.invoices.forEach((x) => toast[["sent", "generated"].includes(x.status) ? "success" : "warning"](`Billing ${x.invoice_no}: ${x.status}${x.error ? ` - ${x.error}` : ""}`));
      } catch (err) { toast.error(`${f.name}: ${errMsg(err)}`); }
    }
    setBusy(false);
    load();
  };
  return (
    <Panel title="SAP billing IDocs (INVOIC02) -> invoice to customer" testId="sap-invoices-panel"
      action={<label className={`inline-flex h-8 cursor-pointer items-center rounded-md px-3 text-xs ${btn.ghost}`} data-testid="sap-invoices-upload-label"><Upload className="mr-1.5 h-3.5 w-3.5" /> {busy ? "Working…" : "Upload billing IDoc"}<input type="file" multiple className="hidden" disabled={busy} onChange={upload} data-testid="sap-invoices-upload-input" /></label>}>
      <p className="px-5 pt-3 text-xs text-slate-500">The server script posts billing IDocs from its INVOIC folder. Each one is matched to its order by PO number and sent straight away as an EDIFACT invoice. Anything that doesn't match, credit notes, or repeats is held here with the reason - send those from the order's INVOIC button.</p>
      {!rows.length ? <p className="px-5 py-4 text-xs text-slate-500" data-testid="sap-invoices-empty">No billing IDocs received yet.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="border-b border-slate-800"><tr><Th>Received</Th><Th>Billing doc</Th><Th>PO</Th><Th>Partner</Th><Th>Result</Th><Th>File</Th><Th></Th></tr></thead>
          <tbody>{rows.map((r) => <Row key={r.id} r={r} load={load} />)}</tbody>
        </table></div>)}
    </Panel>
  );
};
